// Social: public profiles (/u/:username), connections and meetup links (/m/:slug).
// Everything goes through RPCs in supabase/migrations/20261030_social_profiles_connections_meetups.sql:
// the tables themselves are only readable by the people involved, and every
// write is checked server-side (blocks, rate limits, who may share a world).
import { supabase } from '../config/supabase';

/** Same rule as sign-up (components/Auth.tsx) and the Welcome username step. */
export const USERNAME_RE = /^[a-zA-Z0-9_.]{3,20}$/;
export const DISPLAY_NAME_MAX = 40;
export const BIO_MAX = 280;
export const MEETUP_TITLE_MAX = 80;
export const MEETUP_DESC_MAX = 500;

export const profilePath = (username: string) => `/u/${encodeURIComponent(username)}`;
export const meetupPath = (slug: string) => `/m/${encodeURIComponent(slug)}`;
export const meetupUrl = (slug: string) => `${window.location.origin}${meetupPath(slug)}`;

/** Send a signed-out visitor to sign up, then back to `back` (a same-site path). */
export const signupHref = (back: string) => `/login?mode=signup&next=${encodeURIComponent(back)}`;

export type ConnectionDirection = 'mutual' | 'incoming' | 'outgoing';

export interface PublicWorld { id: string; name: string; thumbnail_url: string | null; updated_at: string | null }

export interface PublicProfile {
  id: string;
  username: string;
  display_name: string | null;
  bio: string | null;
  joined_at: string | null;
  avatar_thumb: string | null;
  connection_count: number;
  is_me: boolean;
  blocked: boolean;
  connection: { id: string; status: 'pending' | 'accepted'; direction: ConnectionDirection } | null;
  worlds: PublicWorld[];
}

export interface ConnectionRow {
  id: string;
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_thumb: string | null;
  status: 'pending' | 'accepted';
  direction: ConnectionDirection;
  created_at: string;
}

export interface Meetup {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  starts_at: string | null;
  timezone: string | null;
  cancelled: boolean;
  ended: boolean;
  is_host: boolean;
  host: { username: string | null; display_name: string | null; avatar_thumb: string | null };
  world: { id: string; name: string; thumbnail_url: string | null; is_public: boolean };
  can_enter: boolean;
}

export interface MyMeetup {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  starts_at: string | null;
  timezone: string | null;
  cancelled_at: string | null;
  created_at: string;
  world_id: string;
  world_name: string;
  world_thumbnail: string | null;
  joined: number;
}

/** The server's own messages are written for people; anything else gets a generic line. */
function friendly(error: { message: string; hint?: string; code?: string } | null, fallback: string): Error {
  const hint = (error as { hint?: string } | null)?.hint;
  if (error && hint && ['auth_required', 'bad_input', 'not_allowed', 'rate_limited', 'missing', 'gone'].includes(hint)) {
    return new Error(error.message);
  }
  if (error && /could not find the function|does not exist/i.test(error.message)) {
    return new Error('This feature is being switched on. Try again shortly.');
  }
  return new Error(fallback);
}

export const displayName = (p: { display_name?: string | null; username?: string | null }) =>
  (p.display_name && p.display_name.trim()) || p.username || 'GathrXR member';

// ---- Profiles ----

export async function getPublicProfile(username: string): Promise<PublicProfile | null> {
  const { data, error } = await supabase.rpc('get_public_profile', { p_username: username });
  if (error) throw friendly(error, 'Couldn’t load this profile. Please try again.');
  return (data as PublicProfile | null) ?? null;
}

// ---- Connections ----

export async function requestConnection(userId: string): Promise<'pending' | 'accepted'> {
  const { data, error } = await supabase.rpc('request_connection', { p_user: userId });
  if (error) throw friendly(error, 'Couldn’t send that request. Please try again.');
  return data as 'pending' | 'accepted';
}

export async function respondConnection(id: string, accept: boolean): Promise<void> {
  const { error } = await supabase.rpc('respond_connection', { p_id: id, p_accept: accept });
  if (error) throw friendly(error, 'Couldn’t update that request. Please try again.');
}

/** Remove a connection, or cancel your own pending request. */
export async function removeConnection(userId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_connection', { p_user: userId });
  if (error) throw friendly(error, 'Couldn’t remove that connection. Please try again.');
}

export async function myConnections(): Promise<ConnectionRow[]> {
  const { data, error } = await supabase.rpc('my_connections');
  if (error) throw friendly(error, 'Couldn’t load your connections. Please try again.');
  return (data || []) as ConnectionRow[];
}

/** Requests waiting for the signed-in user (0 on any error, e.g. before the migration). */
export async function incomingRequestCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('connections')
    .select('id', { count: 'exact', head: true })
    .eq('addressee_id', userId)
    .eq('status', 'pending');
  return error ? 0 : count || 0;
}

// ---- Meetups ----

export async function createMeetup(input: {
  worldId: string; title: string; description?: string; startsAt?: string | null; timezone?: string | null;
}): Promise<{ id: string; slug: string }> {
  const { data, error } = await supabase.rpc('create_meetup', {
    p_world: input.worldId,
    p_title: input.title,
    p_description: input.description || null,
    p_starts_at: input.startsAt || null,
    p_timezone: input.timezone || null,
  });
  if (error) throw friendly(error, 'Couldn’t create the meetup. Please try again.');
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.slug) throw new Error('Couldn’t create the meetup. Please try again.');
  return row as { id: string; slug: string };
}

export async function cancelMeetup(id: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_meetup', { p_id: id });
  if (error) throw friendly(error, 'Couldn’t cancel the meetup. Please try again.');
}

export async function getMeetup(slug: string): Promise<Meetup | null> {
  const { data, error } = await supabase.rpc('get_meetup', { p_slug: slug });
  if (error) throw friendly(error, 'Couldn’t load this meetup. Please try again.');
  return (data as Meetup | null) ?? null;
}

/** Join a meetup; returns the world id to open (private worlds grant visitor access). */
export async function joinMeetup(slug: string): Promise<string> {
  const { data, error } = await supabase.rpc('join_meetup', { p_slug: slug });
  if (error) throw friendly(error, 'Couldn’t join the meetup. Please try again.');
  if (!data) throw new Error('Couldn’t join the meetup. Please try again.');
  return data as string;
}

export async function myMeetups(): Promise<MyMeetup[]> {
  const { data, error } = await supabase.rpc('my_meetups');
  if (error) throw friendly(error, 'Couldn’t load your meetups. Please try again.');
  return (data || []) as MyMeetup[];
}

// ---- Time helpers ----

/** The browser's IANA time zone, e.g. "America/New_York". */
export const localTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

/** Every zone the browser knows (falls back to just the local one). */
export function timeZones(): string[] {
  const local = localTimeZone();
  try {
    const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone');
    if (all && all.length) return all.includes(local) ? all : [local, ...all];
  } catch { /* older browsers */ }
  return local === 'UTC' ? ['UTC'] : [local, 'UTC'];
}

/** Offset (ms) of `tz` from UTC at instant `at`. */
function zoneOffset(at: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at);
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour') % 24, n('minute'), n('second'));
  return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/**
 * A wall-clock time typed into <input type="datetime-local"> ("2026-11-02T19:30"),
 * read in time zone `tz`, as an ISO instant. Null if it can't be read.
 */
export function wallTimeToIso(value: string, tz: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  try {
    let t = guess - zoneOffset(new Date(guess), tz);
    t = guess - zoneOffset(new Date(t), tz); // second pass settles DST edges
    return new Date(t).toISOString();
  } catch {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
}

/** "Sat, Nov 2, 7:30 PM" in the viewer's own time (or in `tz` when given). */
export function formatWhen(iso: string, tz?: string): string {
  const d = new Date(iso);
  try {
    return d.toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      ...(tz ? { timeZone: tz } : {}),
    });
  } catch {
    return d.toLocaleString();
  }
}

/** "EDT", "GMT+1"… for the viewer's zone (or `tz`). */
export function zoneLabel(iso: string, tz?: string): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short', ...(tz ? { timeZone: tz } : {}) })
      .formatToParts(new Date(iso));
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

export const formatJoined = (iso: string | null) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); } catch { return ''; }
};
